import React, {
	PropsWithChildren,
	createContext,
	memo,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { LatLngLiteral } from "leaflet";
import { useInternal } from "uikit";

import { Language, Map as Search } from "../../../../../services";
import { useTypedSelector } from "../../../../../redux/store";

export const PointOrderModalTableContext =
	createContext<Provider.Context | null>(null);

export const usePointOrderModalTableContext = (): Provider.Context => {
	const store = useContext<Provider.Context | null>(
		PointOrderModalTableContext,
	);
	if (!store) {
		throw new Error(
			"Missing PointOrderModalTableContext.Provider in the tree",
		);
	}
	return store;
};

export const PointOrderModalTableProvider: React.FC<Provider.Props> = ({
	children,
	taxiServiceId,
}): JSX.Element => {
	const lang = useTypedSelector((state) => state.session.language);

	const [baseTaxiServiceId, setBaseTaxiServiceId] =
		useState<Provider.Value["rowId"]>(-1);
	const [rowId, setRowId] = useInternal<Provider.Value["rowId"]>(-1);
	const [reload, setReload] = useInternal<Provider.Value["reload"]>(false);
	const [reloadCell, setReloadCell] =
		useInternal<Provider.Value["reloadCell"]>("");
	const [disabled, setDisabled] =
		useInternal<Provider.Value["disabled"]>(false);
	const [customerId, setCustomerId] =
		useInternal<Provider.Value["customerId"]>(-1);
	// value

	const [settlements, setSettlements] = useState<
		Provider.Value["settlements"]
	>(new Map());
	const [streets, setStreets] = useState<Provider.Value["streets"]>(
		new Map(),
	);
	const [settlementsCoordinates, setSettlementsCoordinates] = useState<
		Provider.Value["mapCoordinates"]
	>(new Map());
	const [streetsCoordinates, setStreetsCoordinates] = useState<
		Provider.Value["mapCoordinates"]
	>(new Map());
	const [housesCoordinates, setHousesCoordinates] = useState<
		Provider.Value["mapCoordinates"]
	>(new Map());
	const [hadChangeStreets, setHadChangeStreets] = useState<
		Provider.Value["hadChangeStreets"]
	>(new Map());

	const getData = useCallback<Provider.Context["getData"]>(
		(id) => {
			if (id) {
				const settlement = settlements.get(id);
				const street = streets.get(id);
				const settlementCoordinates = settlementsCoordinates.get(id);
				const streetCoordinates = streetsCoordinates.get(id);
				const houseCoordinates = housesCoordinates.get(id);
				const hadChangeStreet = hadChangeStreets.get(id);

				setReload(false);
				setRowId(-1);

				return {
					street,
					settlement,
					streetCoordinates,
					settlementCoordinates,
					houseCoordinates,
					hadChangeStreet,
					//
					cityCellState: undefined,
					streetCellState: undefined,
				};
			}
			setRowId(-1);
			setReload(false);
			return {
				street: undefined,
				settlement: undefined,
				hadChangeStreet: undefined,
				//
				cityCellState: undefined,
				streetCellState: undefined,
			};
		},
		[
			hadChangeStreets,
			housesCoordinates,
			setReload,
			setRowId,
			settlements,
			settlementsCoordinates,
			streets,
			streetsCoordinates,
		],
	);

	const reset = useCallback<Provider.Context["reset"]>(
		(id: number) => {
			streets.set(id, undefined);
			streetsCoordinates.set(id, undefined);
			housesCoordinates.set(id, undefined);
			setRowId(id);
			setReload(true);
		},
		[housesCoordinates, setReload, setRowId, streets, streetsCoordinates],
	);

	const addStreet = useCallback<Provider.Context["addStreet"]>(
		(streetObject: Search.Search.Object | undefined, id: number) => {
			setStreets((prev) => prev.set(id, streetObject));
			setStreetsCoordinates((prev) =>
				prev.set(id, streetObject?.coordinates),
			);
			setReload(true);
			setRowId(id);
		},
		[setReload, setRowId, setStreetsCoordinates],
	);

	const handleChangeStreet = useCallback<
		Provider.Context["handleChangeStreet"]
	>(
		(id) => {
			if (!id) return;

			setHadChangeStreets((prev) => {
				const next = new Map(prev);
				const prevValue = next.get(id) ?? 0;
				next.set(id, prevValue + 1);
				return next;
			});

			setReload(true);
			setRowId(id);
		},
		[setReload, setRowId],
	);

	const addSettlement = useCallback<Provider.Context["addSettlement"]>(
		(settlementObject, id) => {
			if (!settlementObject) {
				reset(id);
				return;
			}

			setSettlements((prev) => prev.set(id, settlementObject));
			setSettlementsCoordinates((prev) =>
				prev.set(id, settlementObject.coordinates),
			);
			setStreets((prev) => prev.set(id, undefined));
			setStreetsCoordinates((prev) => prev.set(id, undefined));
			setReload(true);
			setRowId(id);
		},
		[reset, setReload, setRowId, setStreetsCoordinates],
	);

	const addSettlementCoordinates = useCallback<
		Provider.Context["addSettlementCoordinates"]
	>(
		(coordinates, id) => {
			setSettlementsCoordinates((prev) => prev.set(id, coordinates));
			setRowId(id);
			setReload(true);
		},
		[setReload, setRowId],
	);

	const addStreetCoordinates = useCallback<
		Provider.Context["addStreetCoordinates"]
	>(
		(coordinates, id) => {
			setStreetsCoordinates((prev) => prev.set(id, coordinates));
			setRowId(id);
			setReload(true);
		},
		[setReload, setRowId],
	);

	const addHouseCoordinates = useCallback<
		Provider.Context["addHouseCoordinates"]
	>(
		(coordinates, id) => {
			setHousesCoordinates((prev) => prev.set(id, coordinates));
			setRowId(id);
			setReload(true);
		},
		[setReload, setRowId],
	);

	useEffect(() => {
		if (baseTaxiServiceId === -1 && taxiServiceId) {
			setBaseTaxiServiceId(taxiServiceId);
		}

		if (
			baseTaxiServiceId !== -1 &&
			taxiServiceId &&
			baseTaxiServiceId !== taxiServiceId
		) {
			setBaseTaxiServiceId(taxiServiceId);

			settlements.forEach((item) => {
				if (!item?.save) {
					settlements.delete(Number(item?.id) || 0);
					settlementsCoordinates.delete(Number(item?.id) || 0);
				}
			});
		}
	}, [baseTaxiServiceId, settlements, settlementsCoordinates, taxiServiceId]);

	return (
		<PointOrderModalTableContext.Provider
			value={useMemo(
				() => ({
					reload,
					getData,

					addSettlement,
					addStreet,
					handleChangeStreet,
					addSettlementCoordinates,
					addStreetCoordinates,
					addHouseCoordinates,
					rowId,
					setRowId,

					disabled,
					setDisabled,

					customerId,
					setCustomerId,

					reset,
					lang,
					reloadCell,
					setReloadCell,
					baseTaxiServiceId,
					setBaseTaxiServiceId,
				}),
				[
					reloadCell,
					setReloadCell,
					reload,
					getData,
					addSettlement,
					addStreet,
					handleChangeStreet,
					addSettlementCoordinates,
					addStreetCoordinates,
					addHouseCoordinates,
					rowId,
					setRowId,
					disabled,
					setDisabled,
					customerId,
					setCustomerId,
					reset,
					lang,

					baseTaxiServiceId,
					setBaseTaxiServiceId,
				],
			)}
		>
			{children}
		</PointOrderModalTableContext.Provider>
	);
};

export declare namespace Provider {
	export interface Props extends PropsWithChildren {
		taxiServiceId?: number;
	}

	type Settlement = Search.Search.Object;
	type Street = Search.Search.Object;
	interface GetData {
		settlement: Settlement | undefined;
		street: Street | undefined;
		streetCoordinates?: LatLngLiteral | undefined;
		settlementCoordinates?: LatLngLiteral | undefined;
		houseCoordinates?: LatLngLiteral | undefined;
		hadChangeStreet?: number | undefined;
		cityCellState?: CellInputState | undefined;
		streetCellState?: CellInputState | undefined;
	}
	interface Value {
		disabled: boolean;
		customerId: number;
		rowId: number;
		reload: boolean;
		reloadCell: string;

		mapCoordinates: Map<number, LatLngLiteral | undefined>;
		settlements: Map<number, (Settlement & { save?: boolean }) | undefined>;
		streets: Map<number, Street | undefined>;
		hadChangeStreets: Map<number, number | undefined>;
	}
	interface Context {
		lang: Language;
		reset: (id: number) => void;
		getData: (id: number) => GetData;

		reload: boolean;
		reloadCell: string;
		setReloadCell: React.Dispatch<React.SetStateAction<string>>;

		baseTaxiServiceId: number;
		setBaseTaxiServiceId: React.Dispatch<React.SetStateAction<number>>;

		addSettlement: (settlement: Settlement | undefined, id: number) => void;
		addStreet: (street: Street | undefined, id: number) => void;

		handleChangeStreet: (id: number) => void;

		addSettlementCoordinates: (
			coordinates: LatLngLiteral | undefined,
			id: number,
		) => void;
		addStreetCoordinates: (
			coordinates: LatLngLiteral | undefined,
			id: number,
		) => void;

		addHouseCoordinates: (
			coordinates: LatLngLiteral | undefined,
			id: number,
		) => void;

		rowId: number;
		setRowId: (value: number) => void;

		disabled: boolean;
		setDisabled: (value: boolean) => void;

		customerId?: number;
		setCustomerId: (value: number) => void;

		// setCellState: SetCellState;
	}

	type CellInputState = {
		isManual: boolean; //* The last character was manually entered in the cell
		isSelected: boolean; //* The last event was due to the user selecting in the selector in the cell
	};

	interface SetCellStateOptions {
		typeCell: "city" | "street";
		typeElement: "isManual" | "isSelected";
		reset?: boolean;
	}

	type SetCellState = (id: number, options: SetCellStateOptions) => void;
}

export const PointOrderModalTableProviderMemo = memo(
	PointOrderModalTableProvider,
);

export const ConstantAccessors = {
	id: "id",
	city: "city",
	house: "house",
	street: "street",
	entrance: "entrance",
	flat: "flat",
	polygon: "polygon",
	setting: "setting",
} as const;
export type TypeAccessors = typeof ConstantAccessors;
export type ValueAccessors =
	(typeof ConstantAccessors)[keyof typeof ConstantAccessors];
